import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const OPENWEATHER_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY || '94daa3b20d13e46de4174ce9f85bf37d';

const formatDateTime = (date) => {
  try {
    const fmtDay = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(date);
    const fmtNum = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
    const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
    return { weekday: fmtDay, dayMonth: fmtNum, time };
  } catch (_) {
    // Fallback simples
    return { weekday: '', dayMonth: '', time: '' };
  }
};

const WeatherCard = () => {
  const { user } = useAuth();
  const companyId = user?.company_id || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [weather, setWeather] = useState(null);
  const [now, setNow] = useState(new Date());

  const COMPANY_KEY = useMemo(() => (cid) => `astro_company_settings_${cid}`, []);
  const PROFILE_KEY = useMemo(() => (uid) => `astro_profile_${uid}`, []);
  const PROFILE_GEO_KEY = useMemo(() => (uid) => `astro_profile_geo_${uid}`, []);

  // Atualiza relógio a cada minuto
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const loadCompanySettings = async () => {
    // 1) Tenta localStorage com chave por company_id
    try {
      if (companyId) {
        const raw = localStorage.getItem(COMPANY_KEY(companyId));
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.cidade) {
            setCity(parsed.cidade);
            if (parsed?.estado) setUf(String(parsed.estado).toUpperCase());
            return { cidade: parsed.cidade, estado: parsed?.estado || '' };
          }
        }
      }
    } catch (_) {}

    // 2) Tenta chave genérica (legado)
    try {
      const rawLegacy = localStorage.getItem('astro_company_settings_');
      if (rawLegacy) {
        const parsedLegacy = JSON.parse(rawLegacy);
        if (parsedLegacy?.cidade) {
          setCity(parsedLegacy.cidade);
          if (parsedLegacy?.estado) setUf(String(parsedLegacy.estado).toUpperCase());
          // Se tivermos companyId, sincroniza na chave padronizada
          if (companyId) {
            try { localStorage.setItem(COMPANY_KEY(companyId), JSON.stringify(parsedLegacy)); } catch (_) {}
          }
          return { cidade: parsedLegacy.cidade, estado: parsedLegacy?.estado || '' };
        }
      }
    } catch (_) {}

    // 3) Fallback: buscar do Supabase
    if (!companyId) return null;
    try {
      const { data, error } = await supabase
        .from('configuracoes_empresa')
        .select('company_id, cidade, estado, nome_fantasia, cnpj, logradouro, numero, complemento, bairro, cep, telefone_principal, telefone_secundario, email_contato, site, segmento, descricao, plano_contratado, status_empresa')
        .eq('company_id', companyId)
        .single();
      if (error) throw error;
      if (data) {
        setCity(data.cidade || '');
        if (data?.estado) setUf(String(data.estado).toUpperCase());
        try { localStorage.setItem(COMPANY_KEY(companyId), JSON.stringify(data)); } catch (_) {}
        return { cidade: data.cidade || '', estado: data?.estado || '' };
      }
    } catch (e) {
      console.warn('[WeatherCard] Falha ao buscar configuracoes_empresa no Supabase:', e);
    }

    return null;
  };
  // Localização do usuário (perfil) — prioridade principal
  const loadUserLocation = async () => {
    if (!user?.id) return null;
    const cacheKey = PROFILE_KEY(user.id);
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const c = parsed?.cidade || '';
        const e = (parsed?.estado || '').toString().toUpperCase();
        if (c) {
          setCity(c);
          // tenta pegar geo
          let lat = null, lon = null;
          try {
            const geoRaw = localStorage.getItem(PROFILE_GEO_KEY(user.id));
            if (geoRaw) {
              const geo = JSON.parse(geoRaw);
              lat = geo?.lat ?? null;
              lon = geo?.lon ?? null;
            }
          } catch (_) {}
          return { cidade: c, estado: e, lat, lon };
        }
      }
    } catch (_) {}
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, cidade, estado')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      if (data) {
        const c = data.cidade || '';
        const e = (data.estado || '').toString().toUpperCase();
        if (c) {
          setCity(c);
        }
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch (_) {}
        // lê geo depois de salvar perfil
        let lat = null, lon = null;
        try {
          const geoRaw = localStorage.getItem(PROFILE_GEO_KEY(user.id));
          if (geoRaw) {
            const geo = JSON.parse(geoRaw);
            lat = geo?.lat ?? null;
            lon = geo?.lon ?? null;
          }
        } catch (_) {}
        return { cidade: c, estado: e, lat, lon };
      }
    } catch (e) {
      console.warn('[WeatherCard] Falha ao buscar perfil do usuário no Supabase:', e);
    }
    return null;
  };

  const fetchWeatherByCoords = async (lat, lon) => {
    if (lat == null || lon == null || !OPENWEATHER_KEY) return null;
    const url = `https://api.openweathermap.org/data/2.5/weather?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&units=metric&lang=pt_br&appid=${OPENWEATHER_KEY}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`OpenWeather error: ${res.status}`);
    const json = await res.json();
    return {
      temp: Math.round(json?.main?.temp ?? 0),
      temp_min: Math.round(json?.main?.temp_min ?? 0),
      temp_max: Math.round(json?.main?.temp_max ?? 0),
      desc: json?.weather?.[0]?.description || '',
      icon: json?.weather?.[0]?.icon || '01d',
    };
  };

  const fetchWeather = async (cidade, estadoUF) => {
    if (!cidade || !OPENWEATHER_KEY) return null;
    // OpenWeather só garante state code para US; para BR use apenas cidade + país
    const q = `${cidade},BR`;
    const url = `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(q)}&units=metric&lang=pt_br&appid=${OPENWEATHER_KEY}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`OpenWeather error: ${res.status}`);
    const json = await res.json();
    return {
      temp: Math.round(json?.main?.temp ?? 0),
      temp_min: Math.round(json?.main?.temp_min ?? 0),
      temp_max: Math.round(json?.main?.temp_max ?? 0),
      desc: json?.weather?.[0]?.description || '',
      icon: json?.weather?.[0]?.icon || '01d',
    };
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Aguarda user.id estar presente para ter contexto
        if (!user?.id) {
          setLoading(false);
          return;
        }
        // Usar localização do usuário (perfil). Prioriza lat/lon quando disponível
        const perfil = await loadUserLocation();
        const cidadeAlvo = perfil?.cidade || city;
        const ufAlvo = (perfil?.estado || uf || '').toString().toUpperCase();
        const lat = perfil?.lat ?? null;
        const lon = perfil?.lon ?? null;
        if (!cidadeAlvo && (lat == null || lon == null)) {
          setError('Cidade do usuário não encontrada. Configure em seu Perfil.');
          setLoading(false);
          return;
        }
        const w = (lat != null && lon != null)
          ? await fetchWeatherByCoords(lat, lon)
          : await fetchWeather(cidadeAlvo, ufAlvo);
        if (!isMounted) return;
        setWeather(w);
      } catch (e) {
        if (!isMounted) return;
        setError(e.message || 'Falha ao carregar clima');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, companyId]);

  const { weekday, dayMonth, time } = useMemo(() => formatDateTime(now), [now]);

  return (
    <div className="welcome-inner-card weather-card">
      <div className="weather-top">
        <div className="weather-city">{city || '—'}</div>
        <div className="weather-datetime">{weekday && weekday[0].toUpperCase() + weekday.slice(1)} • {dayMonth} • {time}</div>
      </div>

      {loading && (
        <div className="weather-loading">Carregando clima...</div>
      )}
      {error && !loading && (
        <div className="weather-error">{error}</div>
      )}

      {!loading && !error && weather && (
        <div className="weather-main">
          <div className="weather-left">
            <div className="weather-temp">{weather.temp}<span className="deg">°C</span></div>
            <div className="weather-minmax">
              <span>Mín {weather.temp_min}°</span>
              <span>Máx {weather.temp_max}°</span>
            </div>
            <div className="weather-meta">{weather.desc}</div>
          </div>
          <div className="weather-right">
            <img
              className="weather-icon"
              src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
              alt={weather.desc || 'clima'}
              width={90}
              height={90}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherCard;
